/**
 * JobTimelineSection — the job workspace's merged, reverse-chronological
 * stream: Tharstern milestones/events/despatches/invoices, CIS notes,
 * Tharstern notes, emails, meetings and documents, all in one list. See docs/features/job-workspace-redesign/01_product_brief.md.
 *
 * Visual language follows the existing timeline components
 * (CommunicationTimeline / TicketActivityTimeline) — icon-in-a-circle with a
 * connecting line, a card per entry, badges for state — rather than
 * inventing a third style. The merge/group/filter logic itself is pure and
 * lives in @shared-core/domain/job-timeline; this component is the render +
 * the page's only new write affordance (the note composer) plus the small
 * inline actions (confirm/reject a suggested email or meeting link, upload/
 * download/delete a Tharstern document) that don't have anywhere else to
 * live now that the old Activity tab's separate Notes/Documents/Comms cards
 * are gone.
 *
 * Notes, Tharstern notes and production/delivery/invoice events are fetched
 * here as one merged, server-paginated stream
 * (`listJobTimeline`, spec 08 — see the module docstring on
 * @shared-core/domain/job-timeline for the two-tier split). Emails,
 * meetings and documents stay their own client-merged sources: emails/
 * meetings are passed in as props because the page's `getJobComms` fetch
 * already has them (and the interactive confirm/reject/attach affordances
 * need the complete, unpaginated set); documents are still fetched here
 * (mirroring the old JobDocumentsSection's self-contained load).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { faro } from "@grafana/faro-web-sdk";
import { toast } from "sonner";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Boxes,
  CalendarDays,
  ChevronRight,
  Download,
  ExternalLink,
  Landmark,
  Mail,
  Paperclip,
  Pencil,
  Receipt,
  StickyNote,
  Trash2,
  Truck,
  Upload,
  Wrench,
} from "lucide-react";
import { format } from "date-fns";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import type * as API from "@shared-core/data/api-types";
import type { JobEmailLink, MeetingLink } from "@shared-core/data/adapter";
import {
  buildJobTimeline,
  groupConsecutiveSystemEntries,
  matchesJobTimelineFilter,
  serverKindsForFilter,
  JOB_TIMELINE_FILTERS,
  type JobTimelineEntry,
  type JobTimelineEntryKind,
  type JobTimelineFilterId,
  type JobTimelineRow,
} from "@shared-core/domain/job-timeline";
import { formatBytes } from "@shared-core/domain/formatters";
import { Button } from "../../primitives/button";
import { Badge } from "../../primitives/badge";
import { Input } from "../../primitives/input";
import { cn } from "../../primitives/utils";
import { ListPagination } from "../../patterns/ListPagination";
import {
  AttachEmailDialog,
  emailIdentityKey,
  type EmailCandidate,
} from "./AttachEmailDialog";
import {
  AttachMeetingDialog,
  meetingIdentityKey,
  type MeetingCandidate,
} from "./AttachMeetingDialog";
import {
  ReplyComposer,
  type ReplyComposerJobContext,
} from "../csr-command-centre/ReplyComposer";

export interface JobTimelineSectionProps {
  jobNo: string;
  customerId: string;
  emails: JobEmailLink[];
  meetings: MeetingLink[];
  /** True when the page's `getJobComms` fetch failed. */
  commsFailed?: boolean;
  /** Gates Tharstern-backed content (documents, and the note composer's
   *  "Also create on Tharstern" checkbox) — the same client-side gate
   *  `JobDocumentsSection` uses, so a role that would 403 never sees a
   *  section that blames Tharstern for its own absence. Reading the merged
   *  timeline's `note`/`tharstern_note` entries needs no such gate — that's
   *  `WORK_VISIBILITY_VIEW` + `CUSTOMER_COMMUNICATIONS_READ` server-side,
   *  same as every other kind on this endpoint. */
  canUseTharsternAttachments: boolean;
  /** Job context for the "Draft reply" / "Draft update to customer"
   *  affordances (CSR command-centre Slice 3 — see
   *  docs/features/csr-command-centre/). Optional so callers that don't yet
   *  have the full context (still loading) simply omit the affordance
   *  rather than crashing. */
  job?: ReplyComposerJobContext;
  /** Reports how many entries match the current filter, every time that
   *  number changes. This section owns the merged timeline fetch and the
   *  filter, so it is the only place that knows the true count — the page
   *  uses this for the left-nav badge and its own collapsed-section hint
   *  rather than recomputing a partial count from the props it happens to
   *  hold. */
  onVisibleCountChange?: (count: number) => void;
}

const KIND_ICON: Record<JobTimelineEntryKind, typeof Wrench> = {
  production: Wrench,
  delivery: Truck,
  invoice: Receipt,
  note: StickyNote,
  tharstern_note: Landmark,
  email: Mail,
  meeting: CalendarDays,
  document: Paperclip,
};

function formatEntryTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return `${format(date, "d MMM")} · ${format(date, "HH:mm")}`;
}

// The endpoint's own default (JobTimelineListQuery.page_size) — kept in
// sync explicitly rather than omitted, so a page always requests exactly
// what it will render.
const TIMELINE_PAGE_SIZE = 25;

export function JobTimelineSection({
  jobNo,
  customerId,
  emails,
  meetings,
  commsFailed = false,
  canUseTharsternAttachments,
  job,
  onVisibleCountChange,
}: JobTimelineSectionProps) {
  const dataAdapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The merged, server-paginated stream — notes, Tharstern notes,
  // production/delivery/invoice events (the five kinds
  // `SERVER_TIMELINE_KINDS` names). Emails/meetings/documents are
  // client-merged separately below; see the module docstring on
  // domain/job-timeline.ts for why.
  const [serverEntries, setServerEntries] = useState<
    API.JobTimelineEntryView[]
  >([]);
  const [serverTotal, setServerTotal] = useState(0);
  const [serverTotalPages, setServerTotalPages] = useState(0);
  const [timelineFailed, setTimelineFailed] = useState(false);
  const [page, setPage] = useState(1);

  const [documents, setDocuments] = useState<API.TharsternAttachment[]>([]);
  const [documentsFailed, setDocumentsFailed] = useState(false);
  const [emailItems, setEmailItems] = useState<JobEmailLink[]>(emails);
  const [meetingItems, setMeetingItems] = useState<MeetingLink[]>(meetings);
  const [filter, setFilter] = useState<JobTimelineFilterId>("all");
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(
    new Set(),
  );
  const [expandedEntryIds, setExpandedEntryIds] = useState<Set<string>>(
    new Set(),
  );

  const [noteText, setNoteText] = useState("");
  const [mirrorToTharstern, setMirrorToTharstern] = useState(false);
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Attach / re-link / draft-reply — CSR command-centre Slice 1/2/3
  // affordances (see docs/features/csr-command-centre/), restored from
  // the retired JobCommsSection into this section's inline-action pattern.
  const [relinkingEmailId, setRelinkingEmailId] = useState<string | null>(null);
  const [relinkingMeetingId, setRelinkingMeetingId] = useState<string | null>(
    null,
  );
  const [relinkValue, setRelinkValue] = useState("");
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachMeetingOpen, setAttachMeetingOpen] = useState(false);
  const [jobComposerOpen, setJobComposerOpen] = useState(false);
  const [replyComposerEmail, setReplyComposerEmail] =
    useState<JobEmailLink | null>(null);

  useEffect(() => {
    setEmailItems(emails);
  }, [emails]);
  useEffect(() => {
    setMeetingItems(meetings);
  }, [meetings]);

  // The filter chip picks which kind(s) to request. A filter with no
  // server-sourced kind (emails/meetings/documents) needs no fetch at all —
  // an empty `kind` list would otherwise read to the backend as "every
  // kind" (see `serverKindsForFilter`'s docstring), not "none".
  useEffect(() => {
    if (!jobNo) return;
    const kinds = serverKindsForFilter(filter);
    if (kinds.length === 0) {
      setServerEntries([]);
      setServerTotal(0);
      setServerTotalPages(0);
      setTimelineFailed(false);
      return;
    }
    let cancelled = false;
    setTimelineFailed(false);
    async function load() {
      try {
        const result = await dataAdapter.listJobTimeline(jobNo, {
          page,
          pageSize: TIMELINE_PAGE_SIZE,
          kind: kinds,
        });
        if (cancelled) return;
        setServerEntries(result.items);
        setServerTotal(result.total);
        setServerTotalPages(result.totalPages);
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading job timeline:", err);
        setServerEntries([]);
        setServerTotal(0);
        setServerTotalPages(0);
        setTimelineFailed(true);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, jobNo, filter, page]);

  function changeFilter(next: JobTimelineFilterId) {
    setFilter(next);
    setPage(1);
  }

  async function loadDocuments() {
    setDocumentsFailed(false);
    try {
      const result = await dataAdapter.listTharsternAttachments({
        recordNo: jobNo,
      });
      setDocuments(result.items ?? []);
    } catch {
      setDocumentsFailed(true);
    }
  }

  useEffect(() => {
    if (canUseTharsternAttachments) void loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch keyed on jobNo/canUseTharsternAttachments; loadDocuments closes over current state setters only
  }, [jobNo, canUseTharsternAttachments]);

  async function handlePostNote() {
    const text = noteText.trim();
    if (!text) return;
    setPosting(true);
    try {
      // `create_on_tharstern` mirrors atomically, server-side (specs 06/07)
      // — no more separate createTharsternNote + markNoteMirroredToTharstern
      // round trip. The mirror is best-effort: a Tharstern-side failure
      // still commits the CIS note, just with a null `tharstern_note_id`.
      // The badge below reflects that returned value, never the checkbox
      // the user ticked — assuming success would misreport a failed mirror
      // as having worked.
      const created = await dataAdapter.createNote({
        subject_type: "job",
        subject_id: jobNo,
        subject_label: `Job ${jobNo}`,
        customer_id: customerId,
        note_text: text,
        create_on_tharstern: mirrorToTharstern,
      });
      // Optimistic — only meaningful on page 1, the page the composer's own
      // section is always looking at right after posting.
      if (page === 1) {
        setServerEntries((prev) => [
          {
            id: `local-note-${created.id}`,
            kind: "note",
            occurred_at: created.created_at,
            display_text: created.note_text,
            is_tharstern_sourced: created.tharstern_note_id != null,
            note: created,
          },
          ...prev,
        ]);
      }
      setNoteText("");
      setMirrorToTharstern(false);
    } catch (err) {
      console.error("Error saving note:", err);
    } finally {
      setPosting(false);
    }
  }

  async function handleUploadDocument(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    try {
      await dataAdapter.uploadTharsternAttachment({ recordNo: jobNo, file });
      await loadDocuments();
    } catch (err) {
      console.error("Error uploading document:", err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleDeleteDocument(attachmentId: string) {
    try {
      await dataAdapter.deleteTharsternAttachment(attachmentId, jobNo);
      await loadDocuments();
    } catch (err) {
      console.error("Error deleting document:", err);
    }
  }

  async function handleDownloadDocument(doc: API.TharsternAttachment) {
    try {
      const { blob, filename } =
        await dataAdapter.downloadTharsternAttachment(doc);
      capabilities.downloadBlob(filename || doc.filename || "document", blob);
    } catch (err) {
      console.error("Error downloading document:", err);
    }
  }

  async function handleConfirmEmail(id: string) {
    const previous = emailItems;
    setEmailItems((prev) =>
      prev.map((e) => (e.id === id ? { ...e, link_state: "confirmed" } : e)),
    );
    try {
      await dataAdapter.confirmEmailJobLink(id);
    } catch {
      setEmailItems(previous);
    }
  }

  async function handleRejectEmail(id: string) {
    const previous = emailItems;
    setEmailItems((prev) => prev.filter((e) => e.id !== id));
    try {
      await dataAdapter.rejectEmailJobLink(id);
    } catch {
      setEmailItems(previous);
    }
  }

  async function handleConfirmMeeting(id: string) {
    const previous = meetingItems;
    setMeetingItems((prev) =>
      prev.map((m) => (m.id === id ? { ...m, link_state: "confirmed" } : m)),
    );
    try {
      await dataAdapter.confirmMeetingJobLink(id);
    } catch {
      setMeetingItems(previous);
    }
  }

  async function handleRejectMeeting(id: string) {
    const previous = meetingItems;
    setMeetingItems((prev) => prev.filter((m) => m.id !== id));
    try {
      await dataAdapter.rejectMeetingJobLink(id);
    } catch {
      setMeetingItems(previous);
    }
  }

  async function submitRelinkEmail(id: string) {
    const targetJobNo = relinkValue.trim();
    if (!targetJobNo) return;
    const previous = emailItems;
    setEmailItems((prev) => prev.filter((e) => e.id !== id));
    setRelinkingEmailId(null);
    setRelinkValue("");
    try {
      await dataAdapter.relinkEmailToJob(id, targetJobNo);
      toast.success(`Moved to job #${targetJobNo}`);
    } catch (err) {
      setEmailItems(previous);
      toast.error(
        `Couldn't move this email to job #${targetJobNo} — try again.`,
      );
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("job_timeline.relink_email.failed"),
        { context: { link_id: id, target_job_no: targetJobNo } },
      );
    }
  }

  async function submitRelinkMeeting(id: string) {
    const targetJobNo = relinkValue.trim();
    if (!targetJobNo) return;
    const previous = meetingItems;
    setMeetingItems((prev) => prev.filter((m) => m.id !== id));
    setRelinkingMeetingId(null);
    setRelinkValue("");
    try {
      await dataAdapter.relinkMeetingToJob(id, targetJobNo);
      toast.success(`Moved to job #${targetJobNo}`);
    } catch (err) {
      setMeetingItems(previous);
      toast.error(
        `Couldn't move this meeting to job #${targetJobNo} — try again.`,
      );
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("job_timeline.relink_meeting.failed"),
        { context: { link_id: id, target_job_no: targetJobNo } },
      );
    }
  }

  // Best-effort "already on this job" marking for the attach pickers — see
  // AttachEmailDialog/AttachMeetingDialog's file headers for why this can't
  // be an exact-id check.
  const alreadyLinkedKeys = useMemo(
    () =>
      new Set(
        emailItems.map((e) => emailIdentityKey(e.from_address, e.subject)),
      ),
    [emailItems],
  );
  const alreadyLinkedMeetingKeys = useMemo(
    () => new Set(meetingItems.map((m) => meetingIdentityKey(m.title))),
    [meetingItems],
  );

  async function handleAttach(candidate: EmailCandidate) {
    const optimisticId = candidate.id;
    const optimisticLink: JobEmailLink = {
      id: optimisticId,
      direction: candidate.direction,
      from_name: candidate.from_name,
      from_address: candidate.from_address,
      subject: candidate.subject,
      snippet: candidate.snippet,
      sent_at: candidate.sent_at,
      link_state: "confirmed",
      link_source: "manual",
      matched_job_no: null,
      matched_in: null,
      confidence: null,
      web_link: null,
    };
    setEmailItems((prev) => [...prev, optimisticLink]);
    try {
      await dataAdapter.linkEmailToJob(candidate.id, jobNo);
      // The write result never carries web_link, so refetch instead of
      // applying it to the optimistic row — fills web_link in and refreshes
      // the picker's "already attached" markers too.
      const refreshed = await dataAdapter.getJobComms(jobNo);
      setEmailItems(refreshed.emails);
      toast.success("Email attached to this job");
    } catch (err) {
      setEmailItems((prev) => prev.filter((e) => e.id !== optimisticId));
      toast.error("Couldn't attach this email — try again.");
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("job_timeline.attach_email.failed"),
        { context: { email_id: candidate.id, job_no: jobNo } },
      );
    }
  }

  async function handleAttachMeeting(candidate: MeetingCandidate) {
    const optimisticId = candidate.id;
    const optimisticLink: MeetingLink = {
      id: optimisticId,
      title: candidate.title,
      start_at: candidate.start_at,
      end_at: candidate.end_at,
      location: candidate.location,
      link_state: "confirmed",
      link_source: "manual",
      matched_job_no: null,
      matched_in: null,
      confidence: null,
      web_link: null,
    };
    setMeetingItems((prev) => [...prev, optimisticLink]);
    try {
      await dataAdapter.linkMeetingToJob(candidate.id, jobNo);
      const refreshed = await dataAdapter.getJobComms(jobNo);
      setMeetingItems(refreshed.meetings);
      toast.success("Meeting attached to this job");
    } catch (err) {
      setMeetingItems((prev) => prev.filter((m) => m.id !== optimisticId));
      toast.error("Couldn't attach this meeting — try again.");
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("job_timeline.attach_meeting.failed"),
        { context: { meeting_id: candidate.id, job_no: jobNo } },
      );
    }
  }

  const visibleDocuments = canUseTharsternAttachments ? documents : [];
  const entries = buildJobTimeline({
    serverEntries,
    emails: emailItems,
    meetings: meetingItems,
    documents: visibleDocuments,
    includeLocalSources: page === 1,
  });
  const filtered = entries.filter((e) => matchesJobTimelineFilter(e, filter));
  const rows = groupConsecutiveSystemEntries(filtered);

  // Total under the current filter across every page — `serverTotal`
  // already covers the six server-paginated kinds; emails/meetings/
  // documents are never paginated, so their full (filter-matching) count
  // adds on top without double-counting anything the server already
  // reported.
  const localMatchingCount =
    filter === "all"
      ? emailItems.length + meetingItems.length + visibleDocuments.length
      : filter === "emails"
        ? emailItems.length
        : filter === "meetings"
          ? meetingItems.length
          : filter === "documents"
            ? visibleDocuments.length
            : 0;
  const visibleCount = serverTotal + localMatchingCount;

  // Report the count that matches what's currently on screen (the current
  // filter, and every source this section owns) — not a count the caller
  // would otherwise have to approximate from a partial view of the data.
  useEffect(() => {
    onVisibleCountChange?.(visibleCount);
  }, [visibleCount, onVisibleCountChange]);

  return (
    <div className="space-y-4">
      {/* Composer */}
      <div className="space-y-2 rounded-md border border-border bg-muted/20 p-3">
        <textarea
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[70px] resize-y"
          placeholder="Add a note…"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
        />
        <div className="flex items-center justify-between gap-3">
          {canUseTharsternAttachments ? (
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={mirrorToTharstern}
                onChange={(e) => setMirrorToTharstern(e.target.checked)}
                className="size-3.5 rounded border-input"
              />
              Also create on Tharstern
            </label>
          ) : (
            <span />
          )}
          <Button
            size="sm"
            disabled={!noteText.trim() || posting}
            onClick={() => void handlePostNote()}
          >
            {posting ? "Posting…" : "Post note"}
          </Button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {JOB_TIMELINE_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => changeFilter(f.id)}
              className={cn(
                "rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
                filter === f.id
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/70",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {job && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setJobComposerOpen(true)}
            >
              <Pencil className="size-3.5 mr-1" />
              Draft update to customer
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAttachOpen(true)}
          >
            <Paperclip className="size-3.5 mr-1" />
            Attach an email
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAttachMeetingOpen(true)}
          >
            <Paperclip className="size-3.5 mr-1" />
            Attach a meeting
          </Button>
          {canUseTharsternAttachments && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                className="sr-only"
                aria-label="Upload a document"
                onChange={(e) => void handleUploadDocument(e.target.files?.[0])}
              />
              <Button
                size="sm"
                variant="outline"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="size-3.5 mr-1" />
                {uploading ? "Uploading…" : "Upload document"}
              </Button>
            </>
          )}
        </div>
      </div>

      {job && (
        <ReplyComposer
          job={job}
          open={jobComposerOpen}
          onOpenChange={setJobComposerOpen}
        />
      )}
      <AttachEmailDialog
        open={attachOpen}
        onOpenChange={setAttachOpen}
        customerId={customerId}
        alreadyLinkedKeys={alreadyLinkedKeys}
        onAttach={handleAttach}
      />
      <AttachMeetingDialog
        open={attachMeetingOpen}
        onOpenChange={setAttachMeetingOpen}
        customerId={customerId}
        alreadyLinkedKeys={alreadyLinkedMeetingKeys}
        onAttach={handleAttachMeeting}
      />

      {commsFailed && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Customer emails and meetings could not be loaded.
        </div>
      )}
      {timelineFailed && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Couldn't load this job's timeline. Try again.
        </div>
      )}
      {documentsFailed && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Couldn't reach Tharstern for this job's documents.
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">
          Nothing here yet.
        </p>
      ) : (
        <ol className="space-y-0">
          {rows.map((row) =>
            row.type === "group" ? (
              <TimelineGroupRow
                key={row.id}
                row={row}
                expanded={expandedGroupIds.has(row.id)}
                onToggle={() =>
                  setExpandedGroupIds((prev) => toggleSet(prev, row.id))
                }
              />
            ) : (
              <TimelineEntryRow
                key={row.entry.id}
                entry={row.entry}
                expanded={expandedEntryIds.has(row.entry.id)}
                onToggle={() =>
                  setExpandedEntryIds((prev) => toggleSet(prev, row.entry.id))
                }
                onConfirmEmail={handleConfirmEmail}
                onRejectEmail={handleRejectEmail}
                onConfirmMeeting={handleConfirmMeeting}
                onRejectMeeting={handleRejectMeeting}
                onOpenExternal={(url) => capabilities.openExternalUrl(url)}
                onDownloadDocument={handleDownloadDocument}
                onDeleteDocument={handleDeleteDocument}
                canManageDocuments={canUseTharsternAttachments}
                job={job}
                isRelinkingEmail={relinkingEmailId === row.entry.email?.id}
                isRelinkingMeeting={
                  relinkingMeetingId === row.entry.meeting?.id
                }
                relinkValue={relinkValue}
                onRelinkValueChange={setRelinkValue}
                onOpenRelinkEmail={() => {
                  setRelinkingEmailId(row.entry.email?.id ?? null);
                  setRelinkValue("");
                }}
                onOpenRelinkMeeting={() => {
                  setRelinkingMeetingId(row.entry.meeting?.id ?? null);
                  setRelinkValue("");
                }}
                onCancelRelink={() => {
                  setRelinkingEmailId(null);
                  setRelinkingMeetingId(null);
                  setRelinkValue("");
                }}
                onSubmitRelinkEmail={() =>
                  row.entry.email && submitRelinkEmail(row.entry.email.id)
                }
                onSubmitRelinkMeeting={() =>
                  row.entry.meeting && submitRelinkMeeting(row.entry.meeting.id)
                }
                isReplyComposerOpen={
                  replyComposerEmail?.id === row.entry.email?.id
                }
                onOpenReplyComposer={() =>
                  row.entry.email && setReplyComposerEmail(row.entry.email)
                }
                onReplyComposerOpenChange={(open) =>
                  setReplyComposerEmail(open ? (row.entry.email ?? null) : null)
                }
              />
            ),
          )}
        </ol>
      )}
      {/* Paginates only the six server-sourced kinds — emails/meetings/
          documents already show their complete set on page 1 (see
          `includeLocalSources` above), so they're unaffected by paging
          through the rest. */}
      <ListPagination
        page={page}
        totalPages={serverTotalPages}
        onPageChange={setPage}
      />
    </div>
  );
}

function toggleSet(set: Set<string>, id: string): Set<string> {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

function TimelineGroupRow({
  row,
  expanded,
  onToggle,
}: {
  row: Extract<JobTimelineRow, { type: "group" }>;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <li className="relative flex gap-3 pb-1">
      <div className="absolute left-[15px] top-9 bottom-0 w-px bg-border" />
      <div className="relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-border bg-background text-muted-foreground">
        <Boxes className="size-4" />
      </div>
      <button
        type="button"
        onClick={onToggle}
        className="flex-1 rounded-lg border border-border p-3 mb-3 text-left hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium">
            {row.entries.length} production updates
          </span>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            {formatEntryTime(row.occurredAt)}
            <ChevronRight
              className={cn(
                "size-3.5 transition-transform",
                expanded && "rotate-90",
              )}
            />
          </span>
        </div>
        {expanded && (
          <ul className="mt-2 space-y-1.5 border-t border-border pt-2">
            {row.entries.map((entry) => (
              <li
                key={entry.id}
                className="flex items-center justify-between gap-2 text-sm text-muted-foreground"
              >
                {/* Same shrink trap as the entry title above — a flex item
                    needs min-w-0 to give way to its shrink-0 sibling. */}
                <span className="min-w-0 break-words">{entry.displayText}</span>
                <span className="shrink-0 text-xs">
                  {formatEntryTime(entry.occurredAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </button>
    </li>
  );
}

function TimelineEntryRow({
  entry,
  expanded,
  onToggle,
  onConfirmEmail,
  onRejectEmail,
  onConfirmMeeting,
  onRejectMeeting,
  onOpenExternal,
  onDownloadDocument,
  onDeleteDocument,
  canManageDocuments,
  job,
  isRelinkingEmail,
  isRelinkingMeeting,
  relinkValue,
  onRelinkValueChange,
  onOpenRelinkEmail,
  onOpenRelinkMeeting,
  onCancelRelink,
  onSubmitRelinkEmail,
  onSubmitRelinkMeeting,
  isReplyComposerOpen,
  onOpenReplyComposer,
  onReplyComposerOpenChange,
}: {
  entry: JobTimelineEntry;
  expanded: boolean;
  onToggle: () => void;
  onConfirmEmail: (id: string) => void;
  onRejectEmail: (id: string) => void;
  onConfirmMeeting: (id: string) => void;
  onRejectMeeting: (id: string) => void;
  onOpenExternal: (url: string) => void;
  onDownloadDocument: (doc: API.TharsternAttachment) => void;
  onDeleteDocument: (id: string) => void;
  canManageDocuments: boolean;
  /** Job context for "Draft reply" — undefined omits the affordance. */
  job?: ReplyComposerJobContext;
  isRelinkingEmail: boolean;
  isRelinkingMeeting: boolean;
  relinkValue: string;
  onRelinkValueChange: (value: string) => void;
  onOpenRelinkEmail: () => void;
  onOpenRelinkMeeting: () => void;
  onCancelRelink: () => void;
  onSubmitRelinkEmail: () => void;
  onSubmitRelinkMeeting: () => void;
  isReplyComposerOpen: boolean;
  onOpenReplyComposer: () => void;
  onReplyComposerOpenChange: (open: boolean) => void;
}) {
  const Icon = KIND_ICON[entry.kind];
  const isSuggestedEmail =
    entry.kind === "email" && entry.email?.link_state === "suggested";
  const isSuggestedMeeting =
    entry.kind === "meeting" && entry.meeting?.link_state === "suggested";
  const expandable =
    entry.kind === "note" ||
    entry.kind === "tharstern_note" ||
    entry.kind === "email" ||
    entry.kind === "meeting";

  return (
    <li className="relative flex gap-3 pb-1">
      <div className="absolute left-[15px] top-9 bottom-0 w-px bg-border" />
      <div className="relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-border bg-background text-muted-foreground">
        <Icon className="size-4" />
      </div>
      <div
        className={cn(
          "flex-1 rounded-lg border border-border p-3 mb-3 transition-colors",
          expandable && "cursor-pointer hover:bg-muted/30",
        )}
        onClick={expandable ? onToggle : undefined}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              {entry.isTharsternSourced && (
                <Badge variant="neutral">Tharstern</Badge>
              )}
              {entry.kind === "email" && (
                <span
                  className="text-muted-foreground"
                  title={
                    entry.email?.direction === "inbound" ? "Received" : "Sent"
                  }
                >
                  {entry.email?.direction === "inbound" ? (
                    <ArrowDownLeft className="size-3.5" />
                  ) : (
                    <ArrowUpRight className="size-3.5" />
                  )}
                </span>
              )}
              {isSuggestedEmail || isSuggestedMeeting ? (
                <Badge variant="warning">Suggested</Badge>
              ) : null}
              {/* `min-w-0 break-words`, never `truncate`. A timeline
                  entry's display_text IS the note body for note and
                  tharstern_note kinds (see mock-data/job-timeline.ts), and
                  real Tharstern notes run to several hundred characters.
                  `truncate` sets `white-space: nowrap`, and a flex item's
                  default `min-width: auto` then refuses to shrink below
                  that content — so instead of ellipsing, the span grew to
                  its full text width (measured at 1246px in a 1024px
                  viewport) and scrolled the whole page sideways. */}
              <span className="text-sm font-medium min-w-0 break-words">
                {entry.displayText}
              </span>
            </div>
            {entry.kind === "email" && entry.email && (
              <p className="text-xs text-muted-foreground truncate">
                {entry.email.from_name ?? entry.email.from_address} ·{" "}
                {entry.email.snippet}
              </p>
            )}
            {entry.kind === "meeting" && entry.meeting?.location && (
              <p className="text-xs text-muted-foreground">
                {entry.meeting.location}
              </p>
            )}
            {entry.kind === "note" && entry.note?.note_title && (
              <p className="text-xs text-muted-foreground">
                {entry.note.note_title}
              </p>
            )}
            {entry.kind === "document" && entry.document && (
              <p className="text-xs text-muted-foreground">
                {[
                  formatBytes(entry.document.size_bytes ?? 0),
                  entry.document.user_name,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            )}

            {expanded && entry.kind === "note" && entry.note && (
              <p className="mt-2 text-sm whitespace-pre-wrap border-t border-border pt-2">
                {entry.note.note_text}
              </p>
            )}
            {expanded &&
              entry.kind === "tharstern_note" &&
              entry.tharsternNote && (
                <p className="mt-2 text-sm whitespace-pre-wrap border-t border-border pt-2">
                  {entry.tharsternNote.note_text} —{" "}
                  <span className="text-muted-foreground">
                    {entry.tharsternNote.created_by}
                  </span>
                </p>
              )}
            {expanded && entry.kind === "email" && entry.email && (
              <div className="mt-2 space-y-2 border-t border-border pt-2">
                <p className="text-sm text-muted-foreground bg-muted/50 rounded p-2">
                  {entry.email.snippet}
                </p>
                {entry.email.web_link && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenExternal(entry.email!.web_link!);
                    }}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <ExternalLink className="size-3" />
                    Open in Outlook
                  </button>
                )}
              </div>
            )}
            {expanded &&
              entry.kind === "meeting" &&
              entry.meeting?.web_link && (
                <div className="mt-2 border-t border-border pt-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenExternal(entry.meeting!.web_link!);
                    }}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <ExternalLink className="size-3" />
                    Open in Outlook
                  </button>
                </div>
              )}
          </div>
          <span className="shrink-0 text-xs text-muted-foreground whitespace-nowrap">
            {formatEntryTime(entry.occurredAt)}
          </span>
        </div>

        {entry.kind === "email" && entry.email && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {job && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenReplyComposer();
                }}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <Pencil className="size-3" />
                Draft reply
              </button>
            )}
            {isSuggestedEmail && !isRelinkingEmail && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    onConfirmEmail(entry.email!.id);
                  }}
                >
                  Confirm
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRejectEmail(entry.email!.id);
                  }}
                >
                  Not this job
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenRelinkEmail();
                  }}
                >
                  Link to another job…
                </Button>
              </>
            )}
          </div>
        )}
        {isRelinkingEmail && entry.kind === "email" && (
          <div
            className="flex items-center gap-2 mt-2"
            onClick={(e) => e.stopPropagation()}
          >
            <Input
              value={relinkValue}
              onChange={(e) => onRelinkValueChange(e.target.value)}
              placeholder="Job number"
              className="h-8 w-32 text-sm"
              autoFocus
            />
            <Button
              size="sm"
              onClick={onSubmitRelinkEmail}
              disabled={!relinkValue.trim()}
            >
              Move
            </Button>
            <Button size="sm" variant="ghost" onClick={onCancelRelink}>
              Cancel
            </Button>
          </div>
        )}
        {job && entry.kind === "email" && entry.email && (
          <div className="mt-2" onClick={(e) => e.stopPropagation()}>
            <ReplyComposer
              job={job}
              to={{
                name: entry.email.from_name ?? entry.email.from_address,
                email: entry.email.from_address,
              }}
              inReplyTo={{ id: entry.email.id }}
              open={isReplyComposerOpen}
              onOpenChange={onReplyComposerOpenChange}
            />
          </div>
        )}
        {isSuggestedMeeting && entry.meeting && !isRelinkingMeeting && (
          <div className="mt-2 flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onConfirmMeeting(entry.meeting!.id);
              }}
            >
              Confirm
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onRejectMeeting(entry.meeting!.id);
              }}
            >
              Not this job
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onOpenRelinkMeeting();
              }}
            >
              Link to another job…
            </Button>
          </div>
        )}
        {isRelinkingMeeting && entry.kind === "meeting" && (
          <div
            className="flex items-center gap-2 mt-2"
            onClick={(e) => e.stopPropagation()}
          >
            <Input
              value={relinkValue}
              onChange={(e) => onRelinkValueChange(e.target.value)}
              placeholder="Job number"
              className="h-8 w-32 text-sm"
              autoFocus
            />
            <Button
              size="sm"
              onClick={onSubmitRelinkMeeting}
              disabled={!relinkValue.trim()}
            >
              Move
            </Button>
            <Button size="sm" variant="ghost" onClick={onCancelRelink}>
              Cancel
            </Button>
          </div>
        )}
        {entry.kind === "document" && entry.document && (
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDownloadDocument(entry.document!);
              }}
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <Download className="size-3.5" />
              Download
            </button>
            {canManageDocuments && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteDocument(entry.document!.id);
                }}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="size-3.5" />
                Remove
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}
